import React, { createContext, useContext, useEffect, useState } from 'react'
import { authApi } from './api'
import type { AuthState, User } from '@/types/auth'

const USER_STORAGE_KEY = 'user'

function getStoredUser(): User | null {
  if (typeof window === 'undefined') return null
  const stored = localStorage.getItem(USER_STORAGE_KEY)
  if (!stored) return null
  try {
    return JSON.parse(stored) as User
  } catch (error) {
    localStorage.removeItem(USER_STORAGE_KEY)
    return null
  }
}

function storeUser(user: User) {
  if (typeof window === 'undefined') return
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user))
}

function clearStoredUser() {
  if (typeof window === 'undefined') return
  localStorage.removeItem(USER_STORAGE_KEY)
}

interface AuthContextType extends AuthState {
  login: (user: User) => void
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>(() => {
    const storedUser = getStoredUser()
    return {
      user: storedUser,
      isAuthenticated: Boolean(storedUser),
      isLoading: true,
    }
  })

  useEffect(() => {
    let isMounted = true

    const initialiseSession = async () => {
      try {
        await authApi.csrf()
        const response = await authApi.me()
        if (!isMounted) return

        const user = response.data.user
        storeUser(user)
        setAuthState({
          user,
          isAuthenticated: true,
          isLoading: false,
        })
      } catch (error) {
        if (!isMounted) return
        clearStoredUser()
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        })
      }
    }

    initialiseSession()

    return () => {
      isMounted = false
    }
  }, [])

  const login = (user: User) => {
    storeUser(user)
    setAuthState({
      user,
      isAuthenticated: true,
      isLoading: false,
    })
  }

  const logout = async () => {
    try {
      await authApi.csrf()
    } catch (error) {
      // If we fail to fetch a fresh CSRF token we still attempt the logout request.
      console.debug('Unable to refresh CSRF token before logout.', error)
    }

    try {
      await authApi.logout()
    } catch (error) {
      // Swallow network errors; local state is authoritative for the UI.
      console.debug('Logout request failed, clearing local auth state anyway.', error)
    } finally {
      clearStoredUser()
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      })
    }
  }

  const value: AuthContextType = {
    ...authState,
    login,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
